import { Box, Flex, Stack, Text, useBreakpointValue, useTheme } from '@chakra-ui/react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import ButtonNavigation from 'components/button-navigation'
import ButtonPrimary from 'components/button-primary'
import Heading from 'components/heading'
import Input from 'components/input'
import NotificationError from 'components/notification-error'
import PasswordReset from 'components/password-reset'
import { setPassword } from 'utils/auth-utils'
import { isEmailAddress } from 'utils/string-utils'

import { useResetPassword } from 'api'
import { HELP_REQUEST_LINK } from 'settings'

import type { PasswordResetCallback } from 'components/password-reset'

export default function ForgotPassword() {
  const theme = useTheme()
  const { state } = useLocation()
  const navigate = useNavigate()
  const isSmallViewport = useBreakpointValue({ base: true, md: false })
  const [activeScreen, setActiveScreen] = useState(0)
  const [username, setUsername] = useState(state?.username ?? '')
  const [loading, setLoading] = useState(false)
  const [errorRecoveryCode, setErrorRecoveryCode] = useState(false)

  const { mutateAsync: resetPassword } = useResetPassword()

  const sendRecoveryCode = async () => {
    setLoading(true)
    const recoveryCodeResponse = await resetPassword({ username })
    setLoading(false)
    if (!recoveryCodeResponse) {
      setErrorRecoveryCode(true)
    } else {
      setErrorRecoveryCode(false)
      setActiveScreen(activeScreen + 1)
    }
  }

  const sendNewPassword = async ({ resetCode, newPassword }: PasswordResetCallback) => {
    await setPassword({ username, resetCode, newPassword })
    setLoading(false)
    setActiveScreen(activeScreen + 1)
  }

  return (
    <Flex w="100%" h="100vh" align="center" direction="column" background={theme.baseColors.background.default}>
      <Stack
        w={isSmallViewport ? '100%' : '50%'}
        direction="column"
        spacing="32px"
        my={12}
        px={{ base: 6, xl: 24 }}
        maxW="1280px"
        borderTopRadius="base"
      >
        <Box minH="40px">
          {activeScreen < 2 && (
            <ButtonPrimary
              text="Back"
              icon={{ source: ['far', 'chevron-left'] }}
              color={theme.baseColors.primary.main}
              onClick={() => {
                if (activeScreen === 0) {
                  navigate('/login', { state: { username } })
                } else {
                  setActiveScreen(activeScreen - 1)
                }
              }}
            />
          )}
        </Box>
        {activeScreen === 0 && (
          <>
            <Heading as="h1" text="Forgot Password" />
            <Text>We'll email you a code if your account exists</Text>
            <Input
              type="text"
              label={{ text: 'Email' }}
              value={username}
              onChange={evt => setUsername(evt.target.value)}
            />
            <ButtonNavigation
              w="100%"
              type="progress"
              text="Send reset password code"
              isDisabled={!isEmailAddress(username) || loading}
              onClick={sendRecoveryCode}
            />
          </>
        )}
        {activeScreen === 1 && (
          <PasswordReset
            username={username}
            callback={sendNewPassword}
            loading={loading}
            setLoading={setLoading}
            isForgot
          />
        )}
        {activeScreen === 2 && (
          <>
            <FontAwesomeIcon icon={['fas', 'circle-check']} size="8x" color={theme.baseColors.secondary.dark} />
            <Stack direction="column" spacing="12px">
              <Heading as="h2" text="Success!" />
              <Text>Your password has been changed.</Text>
            </Stack>
            <ButtonNavigation
              w="100%"
              type="progress"
              text="Return to Sign In"
              onClick={() => navigate('/login', { state: { username } })}
            />
          </>
        )}
        {errorRecoveryCode && (
          <NotificationError>
            <p>
              Unable to reset your password. Please login with the temporary password you received via email, or contact{' '}
              <u>
                <a href={HELP_REQUEST_LINK} target="_blank" rel="noreferrer">
                  FrogBox Support
                </a>
              </u>{' '}
              for assistance.
            </p>
          </NotificationError>
        )}
      </Stack>
    </Flex>
  )
}
